import {
  GENDERS,
  GUEST_OPTIONS,
  type GuestDto,
  type GuestGender,
  type GuestOption,
} from '@room-mates/shared';
import { type FormEvent, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { roomLabel } from '../../shared/format.js';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { GenderIcon } from '../../shared/illustrations/GenderIcon.js';
import { GuestAvatar } from '../../shared/illustrations/GuestAvatar.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Button,
  Dialog,
  ErrorAlert,
  Field,
  Select,
  TextArea,
  TextInput,
} from '../../shared/ui/index.js';
import { useAllRooms } from '../houses/houseHooks.js';
import { useCreateGuest, useUpdateGuest } from './guestHooks.js';

export interface GuestDialogProps {
  readonly guest?: GuestDto;
  readonly allGuests: readonly GuestDto[];
  readonly onClose: () => void;
}

export function GuestDialog({ guest, allGuests, onClose }: GuestDialogProps): ReactElement {
  const t = useTranslation();
  const createGuest = useCreateGuest();
  const updateGuest = useUpdateGuest();
  const mutation = guest === undefined ? createGuest : updateGuest;
  const rooms = useAllRooms();

  const [name, setName] = useState(guest?.name ?? '');
  const [notes, setNotes] = useState(guest?.notes ?? '');
  const [gender, setGender] = useState<GuestGender>(guest?.gender ?? 'other');
  const [avatarNonce, setAvatarNonce] = useState(0);
  const [options, setOptions] = useState<GuestOption[]>([...(guest?.options ?? [])]);
  const [roommateIds, setRoommateIds] = useState<string[]>([...(guest?.roommateIds ?? [])]);
  const [incompatibleGuestIds, setIncompatibleGuestIds] = useState<string[]>([
    ...(guest?.incompatibleGuestIds ?? []),
  ]);
  const [incompatibleRoomIds, setIncompatibleRoomIds] = useState<string[]>([
    ...(guest?.incompatibleRoomIds ?? []),
  ]);
  const [tags, setTags] = useState<string[]>([...(guest?.tags ?? [])]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const fieldError = useFieldErrors(mutation.error);

  // A snapshot of every field as it was when the dialog opened, taken once, to
  // tell an edit from a no-op — `avatarNonce` is deliberately excluded, since
  // redrawing the avatar isn't part of what gets saved.
  const [initialSnapshot] = useState(() =>
    JSON.stringify({
      name,
      notes,
      gender,
      options,
      roommateIds,
      incompatibleGuestIds,
      incompatibleRoomIds,
      tags,
    }),
  );
  const isDirty =
    JSON.stringify({
      name,
      notes,
      gender,
      options,
      roommateIds,
      incompatibleGuestIds,
      incompatibleRoomIds,
      tags,
    }) !== initialSnapshot;

  // `Dialog` re-runs its focus/keydown effect whenever `onClose`'s identity
  // changes, so this must stay referentially stable across keystrokes — a
  // plain closure over `isDirty` would flip identity the moment typing first
  // makes the form dirty, stealing focus back to the dialog panel mid-word.
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  });

  // Backdrop click, Escape and the ✕ button all funnel through here; the
  // explicit Cancel button below bypasses this and closes right away, since
  // clicking it already is the deliberate "discard" decision.
  const requestClose = useCallback((): void => {
    if (isDirtyRef.current) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }, [onClose]);

  const knownTags = [...new Set(allGuests.flatMap((candidate) => candidate.tags))].sort();

  // A guest can neither reference themselves nor be wanted and refused at once,
  // so each picker hides what the other has already claimed.
  const others = allGuests.filter((candidate) => candidate.id !== guest?.id);
  const roommateCandidates = others.filter(
    (candidate) => !incompatibleGuestIds.includes(candidate.id),
  );
  const incompatibleCandidates = others.filter((candidate) => !roommateIds.includes(candidate.id));

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const payload = {
      name,
      notes: notes.trim() === '' ? null : notes,
      gender,
      options,
      roommateIds,
      incompatibleGuestIds,
      incompatibleRoomIds,
      tags,
    };
    if (guest === undefined) {
      createGuest.mutate(payload, { onSuccess: onClose });
      return;
    }
    updateGuest.mutate({ guestId: guest.id, input: payload }, { onSuccess: onClose });
  };

  return (
    <>
      <Dialog
        title={guest === undefined ? t.guestDialog.addTitle : t.guestDialog.editTitle(guest.name)}
        onClose={requestClose}
        size="medium"
      >
        <form className="stack" onSubmit={submit} noValidate>
          <ErrorAlert error={mutation.error} />

          <div className="row" style={{ alignItems: 'flex-start' }}>
            {guest === undefined ? null : (
              <div className="row" style={{ alignItems: 'center' }}>
                <GuestAvatar
                  guestId={guest.id}
                  name={guest.name}
                  gender={gender}
                  nonce={avatarNonce}
                  size="xl"
                />
                <Button
                  type="button"
                  variant="ghost"
                  small
                  aria-label={t.guestDialog.regenerateAvatar}
                  title={t.guestDialog.regenerateAvatar}
                  onClick={() => setAvatarNonce((current) => current + 1)}
                >
                  <RegenerateIcon />
                </Button>
              </div>
            )}

            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <Field label={t.guestDialog.nameLabel} error={fieldError('name')}>
                {(props) => (
                  <TextInput
                    {...props}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    autoFocus
                    required
                  />
                )}
              </Field>
            </div>
          </div>

          <div className="field">
            <span className="field__label">{t.guestDialog.genderLabel}</span>
            <span className="field__hint">{t.guestDialog.genderHint}</span>
            <div className="gender-picker">
              {GENDERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={gender === value}
                  onClick={() => setGender(value)}
                >
                  <GenderIcon gender={value} />
                  {t.catalog.gender[value]}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field__label">{t.guestDialog.traitsLabel}</span>
            <span className="field__hint">{t.guestDialog.traitsHint}</span>
            <div className="option-list">
              {GUEST_OPTIONS.map((value) => {
                const selected = options.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    className={`option-chip${selected ? ' option-chip--selected' : ''}`}
                    aria-pressed={selected}
                    title={t.catalog.guestOption[value].description}
                    onClick={() =>
                      setOptions(
                        selected ? options.filter((item) => item !== value) : [...options, value],
                      )
                    }
                  >
                    {t.catalog.guestOption[value].label}
                  </button>
                );
              })}
            </div>
          </div>

          <TagsField tags={tags} onChange={setTags} suggestions={knownTags} />

          <div className="row" style={{ alignItems: 'flex-start' }}>
            <PickerField
              label={t.guestDialog.prefersToRoomWith}
              hint={t.guestDialog.prefersToRoomWithHint}
              selected={roommateIds}
              onChange={setRoommateIds}
              candidates={roommateCandidates.map((candidate) => ({
                value: candidate.id,
                label: candidate.name,
              }))}
            />

            <PickerField
              label={t.guestDialog.mustNotRoomWith}
              hint={t.guestDialog.mustNotRoomWithHint}
              selected={incompatibleGuestIds}
              onChange={setIncompatibleGuestIds}
              candidates={incompatibleCandidates.map((candidate) => ({
                value: candidate.id,
                label: candidate.name,
              }))}
            />
          </div>

          <PickerField
            label={t.guestDialog.mustNotSleepIn}
            hint={t.guestDialog.mustNotSleepInHint}
            selected={incompatibleRoomIds}
            onChange={setIncompatibleRoomIds}
            candidates={(rooms.data ?? []).map((room) => ({
              value: room.id,
              label: roomLabel(room),
            }))}
          />

          <div className="row" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <Field label={t.guestDialog.notesLabel} hint={t.guestDialog.notesHint}>
                {(props) => (
                  <TextArea
                    {...props}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    style={{ minHeight: '3rem' }}
                  />
                )}
              </Field>
            </div>

            <div className="stack stack--tight">
              <Button type="submit" loading={mutation.isPending}>
                {guest === undefined ? t.guestDialog.addGuest : t.guestDialog.saveGuest}
              </Button>
              <Button variant="secondary" onClick={onClose}>
                {t.common.cancel}
              </Button>
            </div>
          </div>
        </form>
      </Dialog>

      {confirmDiscard ? (
        <Dialog title={t.guestDialog.discardTitle} onClose={() => setConfirmDiscard(false)}>
          <p>{t.guestDialog.discardMessage}</p>
          <div className="row row--end" style={{ marginTop: 'var(--space-4)' }}>
            <Button variant="secondary" onClick={() => setConfirmDiscard(false)}>
              {t.guestDialog.keepEditing}
            </Button>
            <Button variant="danger" onClick={onClose}>
              {t.guestDialog.discardChanges}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

/** A circular arrow, for "redraw this avatar". */
function RegenerateIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none">
      <path
        d="M4 12a8 8 0 0 1 13.66-5.66M20 12a8 8 0 0 1-13.66 5.66"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M18.5 3.5V7.5H14.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 20.5V16.5H9.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface TagsFieldProps {
  readonly tags: readonly string[];
  readonly suggestions: readonly string[];
  onChange(next: string[]): void;
}

/** Free-form labels, typed and committed with Enter or a comma. */
function TagsField({ tags, suggestions, onChange }: TagsFieldProps): ReactElement {
  const t = useTranslation();
  const [draft, setDraft] = useState('');

  const commit = (): void => {
    const value = draft.trim().toLowerCase();
    if (value !== '' && !tags.includes(value)) {
      onChange([...tags, value]);
    }
    setDraft('');
  };

  return (
    <div className="field">
      <span className="field__label">{t.guestDialog.tagsLabel}</span>
      <span className="field__hint">{t.guestDialog.tagsHint}</span>

      {tags.length > 0 ? (
        <div className="option-list">
          {tags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="option-chip option-chip--selected"
              onClick={() => onChange(tags.filter((item) => item !== tag))}
            >
              {tag}
              <span aria-hidden="true">✕</span>
              <span className="visually-hidden">{t.guestDialog.remove}</span>
            </button>
          ))}
        </div>
      ) : null}

      <TextInput
        aria-label={t.guestDialog.addATag}
        list="guest-tag-suggestions"
        value={draft}
        placeholder={t.guestDialog.addTagPlaceholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
      />
      <datalist id="guest-tag-suggestions">
        {suggestions
          .filter((tag) => !tags.includes(tag))
          .map((tag) => (
            <option key={tag} value={tag} />
          ))}
      </datalist>
    </div>
  );
}

interface PickerFieldProps {
  readonly label: string;
  readonly hint: string;
  readonly selected: readonly string[];
  readonly candidates: readonly { value: string; label: string }[];
  onChange(next: string[]): void;
}

/** A multi-select built from a dropdown plus removable chips. */
function PickerField({
  label,
  hint,
  selected,
  candidates,
  onChange,
}: PickerFieldProps): ReactElement {
  const t = useTranslation();
  const available = candidates.filter((candidate) => !selected.includes(candidate.value));

  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <span className="field__hint">{hint}</span>

      {selected.length > 0 ? (
        <div className="option-list">
          {selected.map((value) => (
            <button
              key={value}
              type="button"
              className="option-chip option-chip--selected"
              onClick={() => onChange(selected.filter((item) => item !== value))}
            >
              {candidates.find((candidate) => candidate.value === value)?.label ?? value}
              <span aria-hidden="true">✕</span>
              <span className="visually-hidden">{t.guestDialog.remove}</span>
            </button>
          ))}
        </div>
      ) : null}

      {available.length > 0 ? (
        <Select
          aria-label={label}
          value=""
          onChange={(event) => {
            if (event.target.value !== '') {
              onChange([...selected, event.target.value]);
            }
          }}
        >
          <option value="">{t.guestDialog.add}</option>
          {available.map((candidate) => (
            <option key={candidate.value} value={candidate.value}>
              {candidate.label}
            </option>
          ))}
        </Select>
      ) : (
        <span className="field__hint">{t.guestDialog.nothingLeftToChoose}</span>
      )}
    </div>
  );
}
